import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { seedDatabase } from '../../utils/seedData';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const DataSeeder = () => {
  const { hasRole, currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const isAdmin = hasRole('admin');

  const handleTestFirebase = async () => {
    if (!isAdmin) return;

    setLoading(true);
    setMessage('Testing Firebase connection...');

    try {
      // Simple test write
      await setDoc(doc(db, 'test', 'connection'), {
        message: 'Firebase connection test',
        timestamp: new Date(),
        user: currentUser?.email
      });
      setMessage('✅ Firebase connection successful! You can now try creating sample data.');
    } catch (error) {
      console.error('Firebase test error:', error);
      setMessage(`❌ Firebase connection failed: ${error.message || error.code || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    if (!isAdmin) return;

    setLoading(true);
    setMessage('Creating sample data...');

    try {
      await seedDatabase();
      setMessage('✅ Sample data created successfully! You can now test groups, jobs, and projects.');
    } catch (error) {
      console.error('Error seeding data:', error);
      console.error('Error details:', error.message);
      console.error('Error code:', error.code);
      setMessage(`❌ Error creating sample data: ${error.message || error.code || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600 mt-2">You don't have permission to access this tool.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Database Seeder</h1>
        
        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            This tool will create sample data for testing the APN website including:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 mb-6">
            <li><strong>5 Professional Groups</strong> - Investment Banking, Private Equity, M&A, etc.</li>
            <li><strong>3 Group Posts</strong> - Sample discussions and topics</li>
            <li><strong>2 Job Postings</strong> - Investment Banking Analyst, PE Associate</li>
            <li><strong>2 Project Postings</strong> - Financial modeling and due diligence projects</li>
          </ul>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
            <div className="flex">
              <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Note</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  This will create sample data in your Firebase database. Run this only once for testing.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={handleTestFirebase}
            disabled={loading}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Testing...' : 'Test Firebase Connection'}
          </button>

          <button
            onClick={handleSeedData}
            disabled={loading}
            className="bg-apn-blue text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Sample Data...
              </>
            ) : (
              'Create Sample Data'
            )}
          </button>
        </div>

        {message && (
          <div className={`mt-6 p-4 rounded-md ${
            message.includes('✅') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message}
          </div>
        )}

        {message.includes('✅') && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Next Steps:</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Visit <a href="/groups" className="underline">/groups</a> to see the sample groups</li>
              <li>• Visit <a href="/groups/1/admin" className="underline">/groups/1/admin</a> to test group administration</li>
              <li>• Visit <a href="/jobs" className="underline">/jobs</a> to see sample job postings</li>
              <li>• Visit <a href="/projects" className="underline">/projects</a> to see sample projects</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataSeeder;
